import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MessageCircle, Send, X, Sparkles, Loader2, Trash2, Lock, Crown } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUICK_PROMPTS = [
  "Why am I breaking out?",
  "What should I eat for clearer skin?",
  "How can I reduce my stress?",
  "Analyze my skin trends",
  "What's causing my inflammation?",
];

const ChatBubble = ({ message }) => {
  const isUser = message.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`} data-testid={`chat-msg-${message.role}`}>
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-[#FF6B4A] flex items-center justify-center flex-shrink-0 mr-2 mt-1">
          <Sparkles className="w-3.5 h-3.5 text-white" />
        </div>
      )}
      <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
        isUser
          ? 'bg-[#FF6B4A] text-white rounded-br-md'
          : 'bg-[#F4F2EE] text-[#1A1514] rounded-bl-md'
      }`}>
        {message.content.split('\n').map((line, i) => (
          <p key={i} className={i > 0 ? 'mt-2' : ''}>{line}</p>
        ))}
      </div>
    </div>
  );
};

const TypingIndicator = () => (
  <div className="flex justify-start mb-3">
    <div className="w-7 h-7 rounded-full bg-[#FF6B4A] flex items-center justify-center flex-shrink-0 mr-2 mt-1">
      <Sparkles className="w-3.5 h-3.5 text-white" />
    </div>
    <div className="bg-[#F4F2EE] px-4 py-3 rounded-2xl rounded-bl-md">
      <div className="flex gap-1.5">
        <span className="w-2 h-2 bg-[#8C7A76] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-2 h-2 bg-[#8C7A76] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-2 h-2 bg-[#8C7A76] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  </div>
);

const SkinCoachChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [limitReached, setLimitReached] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Load today's history on open
  const loadHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/coach/history`, { withCredentials: true });
      if (res.data.messages?.length > 0) {
        setMessages(res.data.messages);
        setSessionId(res.data.session_id);
      }
    } catch {
      // No history, start fresh
    }
  }, []);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      loadHistory();
    }
  }, [isOpen, messages.length, loadHistory]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading || limitReached) return;

    const userMsg = { role: 'user', content: text.trim(), msg_id: `temp_${Date.now()}` };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/coach/chat`, {
        message: text.trim(),
        session_id: sessionId
      }, { withCredentials: true });

      setSessionId(res.data.session_id);

      if (res.data.limit_reached) {
        setLimitReached(true);
        // Remove the user message we just added since it wasn't processed
        setMessages((prev) => prev.filter(m => m.msg_id !== userMsg.msg_id));
      } else {
        setMessages((prev) => [...prev, { role: 'assistant', content: res.data.response, msg_id: `resp_${Date.now()}` }]);
      }
    } catch {
      setMessages((prev) => [...prev, { role: 'assistant', content: "Sorry, I couldn't process that. Please try again.", msg_id: `err_${Date.now()}` }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setSessionId(null);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#FF6B4A] hover:bg-[#E8552F] text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-xl"
        data-testid="coach-fab"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-0 right-0 sm:bottom-6 sm:right-6 z-50 w-full sm:w-[400px] h-[100dvh] sm:h-[600px] flex flex-col" data-testid="coach-chat">
      <Card className="flex flex-col h-full border-0 sm:rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-[#FF6B4A] px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white text-sm">AI Skin Coach</h3>
              <p className="text-white/60 text-xs">Powered by your data</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={startNewChat} className="p-2 text-white/60 hover:text-white rounded-lg hover:bg-white/10 transition-colors" data-testid="coach-new-chat" title="New chat">
              <Trash2 className="w-4 h-4" />
            </button>
            <button onClick={() => setIsOpen(false)} className="p-2 text-white/60 hover:text-white rounded-lg hover:bg-white/10 transition-colors" data-testid="coach-close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 bg-white">
          {messages.length === 0 && !loading && (
            <div className="text-center py-8">
              <div className="w-14 h-14 rounded-full bg-[#FF6B4A]/10 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-7 h-7 text-[#FF6B4A]" />
              </div>
              <h4 className="font-bold text-[#1A1514] mb-1">Hi! I'm your Skin Coach</h4>
              <p className="text-sm text-[#8C7A76] mb-6">Ask me anything about your skin health. I use your logged data to give personalized advice.</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {QUICK_PROMPTS.map((prompt) => (
                  <button
                    key={prompt}
                    onClick={() => sendMessage(prompt)}
                    className="px-3 py-1.5 bg-[#F4F2EE] hover:bg-[#E5D0C9] text-[#1A1514] text-xs rounded-full transition-colors"
                    data-testid={`quick-prompt-${prompt.slice(0, 10).replace(/\s/g, '-')}`}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg) => (
            <ChatBubble key={msg.msg_id} message={msg} />
          ))}

          {loading && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>

        {/* Limit reached banner */}
        {limitReached && (
          <div className="border-t border-[#E5D0C9] px-4 py-3 bg-[#FFF8F0] flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-[#A388EE]/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Lock className="w-4 h-4 text-[#A388EE]" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-[#1A1514]">Daily limit reached (5/5)</p>
                <p className="text-xs text-[#8C7A76]">Upgrade for unlimited AI coaching</p>
              </div>
              <button
                onClick={() => { setIsOpen(false); document.querySelector('[data-testid="tab-subscription"]')?.click(); }}
                className="bg-[#FF6B4A] text-white text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1"
                data-testid="coach-upgrade-btn"
              >
                <Crown className="w-3 h-3" /> Upgrade
              </button>
            </div>
          </div>
        )}

        {/* Input */}
        <div className="border-t border-[#E5D0C9] p-3 bg-white flex-shrink-0">
          <div className="flex items-center gap-2">
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={limitReached ? "Upgrade for unlimited..." : "Ask about your skin..."}
              className="flex-1 border-[#E5D0C9] rounded-full px-4 h-10 text-sm"
              disabled={loading || limitReached}
              data-testid="coach-input"
            />
            <Button
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || loading || limitReached}
              className="w-10 h-10 p-0 rounded-full bg-[#FF6B4A] hover:bg-[#E8552F] flex-shrink-0"
              data-testid="coach-send"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default SkinCoachChat;
